/* eslint-disable i18next/no-literal-string */
export type YouTubeVideoSource = {
  kind: 'youtube';
  originalUrl: string;
  videoId: string;
  canonicalUrl: string;
  embedUrl: string;
};

export type DirectVideoSource = {
  kind: 'direct';
  originalUrl: string;
  url: string;
};

export type InvalidVideoSource = {
  kind: 'invalid';
  originalUrl: string;
  reason: string;
};

export type VideoSource = YouTubeVideoSource | DirectVideoSource | InvalidVideoSource;

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_HOSTS = new Set(['youtube.com', 'm.youtube.com', 'music.youtube.com']);
const YOUTUBE_EMBED_HOSTS = new Set([
  'youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
]);
const SHORT_YOUTUBE_HOSTS = new Set(['youtu.be']);

const stripWww = (hostname: string): string => hostname.toLowerCase().replace(/^www\./, '');

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value);
  } catch {
    return null;
  }
};

const youtubeIdFromUrl = (url: URL): string | null => {
  const hostname = stripWww(url.hostname);
  const path = url.pathname.split('/').filter(Boolean);

  if (SHORT_YOUTUBE_HOSTS.has(hostname)) {
    return path.length === 1 ? path[0] : null;
  }

  if (!YOUTUBE_EMBED_HOSTS.has(hostname)) return null;

  if (YOUTUBE_HOSTS.has(hostname) && url.pathname.replace(/\/+$/, '') === '/watch') {
    const videoIds = url.searchParams.getAll('v');
    return videoIds.length === 1 ? videoIds[0] : null;
  }

  if (path.length === 2 && (path[0] === 'embed' || (YOUTUBE_HOSTS.has(hostname) && path[0] === 'shorts'))) {
    return path[1] || null;
  }

  return null;
};

const isYouTubeHost = (url: URL): boolean => {
  const hostname = stripWww(url.hostname);
  return YOUTUBE_EMBED_HOSTS.has(hostname) || SHORT_YOUTUBE_HOSTS.has(hostname);
};

const isLocalVideoUrl = (value: string): boolean =>
  value.startsWith('/') || value.startsWith('./') || value.startsWith('../');

/**
 * Extracts the video id from supported YouTube watch, youtu.be, embed and shorts URLs.
 */
export const parseYouTubeVideoId = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = parseUrl(trimmed);
  if (
    !parsed
    || !['http:', 'https:'].includes(parsed.protocol)
    || !isYouTubeHost(parsed)
    || parsed.username
    || parsed.password
    || !['', '80', '443'].includes(parsed.port)
  ) {
    return null;
  }

  const videoId = youtubeIdFromUrl(parsed);
  return videoId && YOUTUBE_ID_PATTERN.test(videoId) ? videoId : null;
};

export const getYouTubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?enablejsapi=1&rel=0`;

/**
 * Classifies a configured source without making a network request. Whether a
 * YouTube owner permits embedding is reported later by the iframe player.
 */
export const classifyVideoSource = (value: string): VideoSource => {
  const originalUrl = value;
  const trimmed = value.trim();

  if (!trimmed) {
    return { kind: 'invalid', originalUrl, reason: 'Introduce un enlace de video.' };
  }

  if (isLocalVideoUrl(trimmed)) {
    return { kind: 'direct', originalUrl, url: trimmed };
  }

  if (/^(?:blob:|data:video\/)/i.test(trimmed)) {
    return { kind: 'direct', originalUrl, url: trimmed };
  }

  const parsed = parseUrl(trimmed);
  if (!parsed) {
    return {
      kind: 'invalid',
      originalUrl,
      reason: 'El enlace no es una URL válida. Usa una dirección https:// o una ruta local.',
    };
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return {
      kind: 'invalid',
      originalUrl,
      reason: 'El enlace debe usar HTTPS, HTTP o una ruta local de video.',
    };
  }

  if (isYouTubeHost(parsed)) {
    if (parsed.username || parsed.password || !['', '80', '443'].includes(parsed.port)) {
      return {
        kind: 'invalid',
        originalUrl,
        reason: 'El enlace de YouTube no es compatible. Usa el enlace público de un video individual sin credenciales ni puertos personalizados.',
      };
    }
    const videoId = youtubeIdFromUrl(parsed);
    if (!videoId || !YOUTUBE_ID_PATTERN.test(videoId)) {
      return {
        kind: 'invalid',
        originalUrl,
        reason: 'El enlace de YouTube no identifica un video compatible. Usa un enlace watch, youtu.be, embed o shorts.',
      };
    }

    return {
      kind: 'youtube',
      originalUrl,
      videoId,
      canonicalUrl: `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}`,
      embedUrl: getYouTubeEmbedUrl(videoId),
    };
  }

  return { kind: 'direct', originalUrl, url: trimmed };
};
